import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  useId,
} from "react";
import { useStandardAgentStore } from "@/store/standardAgentStore";
import { useViewContextStore } from "@/store/viewContextStore";
import { useVisiblePoll } from "@/hooks/useVisiblePoll";
import { ThreadTaskList } from "./ThreadTaskList";
import { getThreadTaskGroups } from "./threadTaskGroups";
import { ThreadWaitTimerBar } from "./ThreadWaitTimerBar";
import {
  AgentTaskStatus,
  AgentWorkflowRunTaskType,
  Permission,
  QuoteStatusEnum,
  type AgentTask,
} from "@forgepoint/shared";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  Send,
  X,
  CheckCircle,
  XCircle,
  Pause,
  RotateCcw,
  Clock,
  ShieldAlert,
  Archive,
  Upload,
  Pencil,
  Check,
  PanelRight,
  GripVertical,
  Eye,
  EyeOff,
} from "lucide-react";
import { ManufacturingSpinner } from "@/components/ManufacturingSpinner";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ThreadReplyComposer } from "./ThreadReplyComposer";
import { BackButton } from "@/components/BackButton";
import { MIN_COLUMN_WIDTH_PX } from "@/components/TwoColumnLayout";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  WorkflowRunViewUserMessage,
  type MovedMessageSource,
} from "@/modules/agents/components/WorkflowRunViewUserMessage";
import { WorkflowRunViewAssistantMessage } from "@/modules/agents/components/WorkflowRunViewAssistantMessage";
import { Input } from "@/components/ui/input";
import {
  MessageNode,
  extractMessageContent,
  isSendRichMessageToolCall,
  buildMessageTree,
  getRichActionType,
  getTurnDurationMs,
  getVisibleStepCount,
  formatTurnDuration,
  groupIntoTurns,
  findPendingScreeningMessage,
} from "./utils";
import { RichMessageDisplay } from "./RichMessageDisplay";
import { RichActionToolCallCard } from "./RichActionToolCallCard";
import { SendMessageToThreadToolCallCard } from "./SendMessageToThreadToolCallCard";
import { AgentWorkTimeline } from "./AgentWorkTimeline";
import type { TimelineStepContext } from "./TimelineStep";
import { TimelineStepList } from "./TimelineStepList";
import { fetchApi } from "@/lib/api";
import { Asset } from "@/store/quoteStore";
import { AssetPreviewDialog } from "@/components/AssetPreviewDialog";
import { CancelTaskConfirmationDialog } from "@/components/CancelTaskConfirmationDialog";
import { captureException } from "@/lib/analytics";
import {
  AGENT_SDK_THREAD_STATUS,
  AGENT_WORKFLOW_RUN_WAIT_TIMER_STATUS,
  AssetSchema,
  TASK_ACTION_ID,
} from "@forgepoint/shared";
import type { AgentSdkMessage } from "@forgepoint/shared";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { RichActionPanel } from "@/modules/agents/components/RichActionPanel";
import {
  mapSdkMessageToRichActionData,
  extractToolCallError,
} from "@/modules/agents/components/helpers/richActionHelpers";
import { ScreeningReviewCard } from "./ScreeningReviewCard";
import { useStandardAgentSettingsStore } from "@/store/standardAgentSettingsStore";
import { useStandardAgentDisplayName } from "../useStandardAgentDisplayName";
import { useTaskDeepLink } from "../useTaskDeepLink";
import { CopyLinkButton } from "@/components/CopyLinkButton";
import { useAuthStore, useHasPermission } from "@/store/authStore";
import {
  useTeamsStore,
  useOrgMembers,
  getUserDisplayName,
} from "@/store/teamsStore";
import type { PublicUser } from "@forgepoint/shared";
import {
  canOpenRichActionPanel,
  canUserApproveAgentTask,
  canUserApproveTask,
} from "@/lib/auth-utils";
import { AddShopRuleButton } from "@/components/agents/rules/AddShopRuleButton";
import { ThreadVisibilityDropdown } from "./ThreadVisibilityDropdown";
import type { OutputAsset, UpdateOpportunityParams } from "@forgepoint/shared";
import {
  ThreadSummaryPanel,
  ThreadSummaryQuoteStatus,
  type ThreadSummaryQuote,
} from "./ThreadSummaryPanel";
import { ThreadQuoteSheet } from "./ThreadQuoteSheet";
import { OpportunityDetailSheet } from "@/modules/opportunities/OpportunityDetailSheet";
import { useOpportunitiesStore } from "@/modules/opportunities/opportunitiesStore";
import { useRecordQuotesStore } from "@/modules/quotes/quotesStore";

const UploadResponseSchema = z.object({
  uploadUrl: z.string(),
  asset: AssetSchema,
});

const mapUploadedAsset = (asset: z.infer<typeof AssetSchema>): Asset => ({
  id: asset.id,
  externalUrl: asset.externalUrl,
  fileName: asset.fileName,
  fileSize: asset.fileSize,
  fileType: asset.fileType,
  createdAt: asset.createdAt.toISOString(),
});

interface StandardAgentThreadViewProps {
  threadId: string | null;
  isOpen: boolean;
  onClose: () => void;
  enableTaskDeepLink?: boolean;
}

const EMPTY_MESSAGES: AgentSdkMessage[] = [];

/** Distance from the top (px) at which scrolling up loads the next older page. */
const LOAD_OLDER_THRESHOLD_PX = 200;
/** How often an idle thread re-checks the server for tasks and turns started elsewhere. */
const IDLE_THREAD_POLL_MS = 30000;
/** A third of the window, so chat and the panel's two columns are even thirds. */
const DEFAULT_CORRESPONDENCE_WIDTH_PERCENT = 33;
const MIN_CORRESPONDENCE_WIDTH_PERCENT = 25;
const MAX_CORRESPONDENCE_WIDTH_PERCENT = 70;
const CORRESPONDENCE_RESIZE_STEP_PERCENT = 5;
const CORRESPONDENCE_WIDTH_STORAGE_KEY =
  "forgepoint-thread-correspondence-width";

/** The panel holds two columns, so it needs room for both of them. */
const MIN_PANEL_WIDTH_PX = MIN_COLUMN_WIDTH_PX * 2;

/** The same floor as `maxCorrespondenceWidth`, held live by the browser. */
const CORRESPONDENCE_MAX_WIDTH_CSS = `max(${MIN_CORRESPONDENCE_WIDTH_PERCENT}%, calc(100% - ${MIN_PANEL_WIDTH_PX}px))`;

/**
 * Widest the correspondence pane may get before the panel beside it drops below
 * the width its two columns need. Falls back to the flat cap while the layout
 * is unmeasured, and never squeezes the pane below its own minimum.
 * @param layoutWidth - Measured width of the row holding both panes, in pixels
 * @returns The maximum correspondence width as a percentage
 */
const maxCorrespondenceWidth = (layoutWidth: number) => {
  if (layoutWidth <= 0) {
    return MAX_CORRESPONDENCE_WIDTH_PERCENT;
  }
  const panelFloor = ((layoutWidth - MIN_PANEL_WIDTH_PX) / layoutWidth) * 100;
  return Math.max(
    MIN_CORRESPONDENCE_WIDTH_PERCENT,
    Math.min(MAX_CORRESPONDENCE_WIDTH_PERCENT, panelFloor),
  );
};

const clampCorrespondenceWidth = (width: number, layoutWidth = 0) =>
  Math.min(
    maxCorrespondenceWidth(layoutWidth),
    Math.max(MIN_CORRESPONDENCE_WIDTH_PERCENT, width),
  );

const readStoredCorrespondenceWidth = () => {
  try {
    const storedWidth = localStorage.getItem(CORRESPONDENCE_WIDTH_STORAGE_KEY);
    if (storedWidth === null) {
      return DEFAULT_CORRESPONDENCE_WIDTH_PERCENT;
    }

    const parsedWidth = Number(storedWidth);
    return Number.isFinite(parsedWidth)
      ? clampCorrespondenceWidth(parsedWidth)
      : DEFAULT_CORRESPONDENCE_WIDTH_PERCENT;
  } catch {
    return DEFAULT_CORRESPONDENCE_WIDTH_PERCENT;
  }
};

const storeCorrespondenceWidth = (width: number) => {
  try {
    localStorage.setItem(CORRESPONDENCE_WIDTH_STORAGE_KEY, String(width));
  } catch {
    // Ignore storage failures; resizing still works for the current view.
  }
};

type PanelSelection =
  | { type: "none" }
  | { type: "task"; taskId: string }
  | { type: "richAction"; messageId: string };

/**
 * Slide-over view of a standard agent thread: correspondence, task panel and summary.
 * @param threadId - The thread to show, or null when none is selected
 * @param isOpen - Whether the sheet is open
 * @param onClose - Called when the user closes the sheet
 * @param enableTaskDeepLink - True when the host owns the URL, so the open task panel syncs with the `taskId` param
 * @returns The thread sheet
 */
export function StandardAgentThreadView({
  threadId,
  isOpen,
  onClose,
  enableTaskDeepLink = false,
}: StandardAgentThreadViewProps) {
  const correspondencePaneId = useId();
  const {
    getThreadById,
    fetchThread,
    fetchMessages,
    updateThreadName,
    addMessage,
    pollMessages,
    pollUntilCompletion,
    stopPolling,
    isSending,
    isLoadingMessages,
    approveTask,
    denyTask,
    approvingTaskId,
    denyingTaskId,
    retryThread,
    isRetrying,
    retryError,
    stopThread,
    cancelWaitTimer,
    fetchThreadVisibility,
    createQuoteReviewTask,
  } = useStandardAgentStore();
  const messagesByThread = useStandardAgentStore(
    (state) => state.messagesByThread,
  );
  const messagesMeta = useStandardAgentStore((state) =>
    threadId ? state.messagesMetaByThread.get(threadId) : undefined,
  );
  const openTasksByThread = useStandardAgentStore(
    (state) => state.openTasksByThread,
  );
  const waitTimersByThread = useStandardAgentStore(
    (state) => state.waitTimersByThread,
  );

  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(),
  );
  const [attachedAssets, setAttachedAssets] = useState<Asset[]>([]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [previewAsset, setPreviewAsset] = useState<Asset | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [panelSelection, setPanelSelection] = useState<PanelSelection>({
    type: "none",
  });
  const [isChatCollapsed, setIsChatCollapsed] = useState(false);
  const [correspondenceWidth, setCorrespondenceWidth] = useState(
    readStoredCorrespondenceWidth,
  );
  const [isResizingPanels, setIsResizingPanels] = useState(false);
  const [isCancellingTimer, setIsCancellingTimer] = useState(false);
  const [isCancelTimerDialogOpen, setIsCancelTimerDialogOpen] = useState(false);
  const [cancelTaskId, setCancelTaskId] = useState<string | null>(null);
  const [showCancelled, setShowCancelled] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(true);
  const [quotePreviewId, setQuotePreviewId] = useState<string | null>(null);
  const [openingQuoteId, setOpeningQuoteId] = useState<string | null>(null);
  const [isOpportunitySheetOpen, setIsOpportunitySheetOpen] = useState(false);
  const [summaryAssetPreview, setSummaryAssetPreview] = useState<{
    id: string;
    fileName: string;
  } | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const threadLayoutRef = useRef<HTMLDivElement | null>(null);
  const correspondenceWidthRef = useRef(correspondenceWidth);
  const isResizingPanelsRef = useRef(false);
  const isAtBottomRef = useRef(true);
  const isProgrammaticScrollRef = useRef(false);
  // Scroll-anchoring state for prepending older history: the first stream item
  // and its offset at fetch time (scrollHeight as fallback), plus the oldest
  // loaded sequence to detect a prepend.
  const scrollAnchorRef = useRef<{
    node: Element | null;
    top: number;
    scrollHeight: number;
  } | null>(null);
  const prevOldestSequenceRef = useRef<number | null>(null);
  const messagesListRef = useRef<HTMLDivElement>(null);
  const programmaticScrollResetRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasTriedInitialAutoOpenRef = useRef(false);
  const shouldWaitForFreshInitialLoadRef = useRef(false);
  const hasObservedFreshInitialLoadRef = useRef(false);
  const activeTurnStartLatestMessageMsRef = useRef<number | null>(null);
  // The openTasks list the completion transition observed, when it saw the
  // selected task still open. The poll writes the terminal status before the
  // final message fetch, so that list can be one commit stale; the re-check
  // effect below finishes the follow-to-replacement once it refreshes.
  const replacementCheckTasksRef = useRef<AgentTask[] | null>(null);
  const isMobile = useIsMobile();
  const standardAgentName = useStandardAgentDisplayName();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const storedWidth = readStoredCorrespondenceWidth();
    correspondenceWidthRef.current = storedWidth;
    setCorrespondenceWidth(storedWidth);
  }, [isOpen]);

  const currentUser = useAuthStore((state) => state.user);
  const currentUserId = currentUser?.id;
  const canApproveAllTasks = useHasPermission(Permission.TASKS_MANAGE);
  const quoteReviewerUserIds = useStandardAgentSettingsStore(
    (state) => state.settings?.quoteReviewerUserIds,
  );
  const getUserById = useTeamsStore((state) => state.getUserById);
  // Subscribes to org members so sender avatars resolve and render.
  useOrgMembers();
  const threadVisibilityData = useStandardAgentStore((state) =>
    threadId ? state.threadVisibility.get(threadId) : undefined,
  );
  const getHydratedOpportunity = useOpportunitiesStore(
    (state) => state.getHydratedOpportunity,
  );
  const getOpportunity = useOpportunitiesStore((state) => state.getOpportunity);
  const quotesById = useRecordQuotesStore((state) => state.quotes);
  const fetchQuotes = useRecordQuotesStore((state) => state.fetchQuotes);
  const updateOpportunity = useOpportunitiesStore(
    (state) => state.updateOpportunity,
  );
  const archiveOpportunity = useOpportunitiesStore(
    (state) => state.archiveOpportunity,
  );
  const unarchiveOpportunity = useOpportunitiesStore(
    (state) => state.unarchiveOpportunity,
  );

  const threadError = useStandardAgentStore((state) =>
    threadId ? (state.threadError.get(threadId) ?? null) : null,
  );

  const thread = threadId ? getThreadById(threadId) : undefined;
  const threadTitle =
    thread && thread.name && thread.name.trim().length > 0
      ? thread.name
      : "Untitled Thread";
  const [isEditingThreadName, setIsEditingThreadName] = useState(false);
  const [threadNameInput, setThreadNameInput] = useState(threadTitle);
  const [isSavingThreadName, setIsSavingThreadName] = useState(false);
  const messages = useMemo(() => {
    if (!threadId) {
      return EMPTY_MESSAGES;
    }
    return messagesByThread.get(threadId) ?? EMPTY_MESSAGES;
  }, [messagesByThread, threadId]);
  const openTasks = useMemo(
    () => (threadId ? (openTasksByThread.get(threadId) ?? []) : []),
    [openTasksByThread, threadId],
  );
  const taskGroups = useMemo(
    () => getThreadTaskGroups(openTasks, currentUserId),
    [openTasks, currentUserId],
  );
  const hasCancelledTask = useMemo(
    () => messages.some((m) => m.task?.status === "CANCELLED"),
    [messages],
  );
  const opportunityId = thread?.opportunityId ?? null;
  const opportunity = opportunityId
    ? getHydratedOpportunity(opportunityId)
    : undefined;

  // Sheets stay mounted per list row when closed; only load data while open.
  useEffect(() => {
    if (isOpen && opportunityId && !opportunity) {
      getOpportunity(opportunityId);
    }
  }, [isOpen, opportunityId, opportunity, getOpportunity]);

  useEffect(() => {
    if (isOpen && opportunityId) {
      fetchQuotes({ opportunityId });
    }
  }, [isOpen, opportunityId, fetchQuotes]);

  const summaryAssets = useMemo(() => {
    const byId = new Map<string, OutputAsset>();
    for (const message of messages) {
      for (const asset of message.assets ?? []) {
        byId.set(asset.id, asset);
      }
    }
    return Array.from(byId.values()).sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [messages]);

  const summaryQuotes = useMemo<ThreadSummaryQuote[]>(() => {
    const pendingTasksByQuoteId = new Map<string, AgentTask>();
    for (const task of openTasks) {
      if (
        task.actionParameters?.actionId === TASK_ACTION_ID.FINALIZE_QUOTE &&
        task.status === AgentTaskStatus.IN_PROGRESS
      ) {
        pendingTasksByQuoteId.set(
          task.actionParameters.parameters.quoteId,
          task,
        );
      }
    }
    // A quote only opens for review when the user can approve its pending task,
    // or is one of the org's quote reviewers for a draft that has no task yet.
    const canReviewQuote = (
      quoteId: string,
      status: ThreadSummaryQuoteStatus,
    ): boolean => {
      if (status === ThreadSummaryQuoteStatus.AWAITING_APPROVAL) {
        return canUserApproveAgentTask(
          currentUser,
          pendingTasksByQuoteId.get(quoteId),
          canApproveAllTasks,
        );
      }
      if (status !== ThreadSummaryQuoteStatus.DRAFT) {
        return false;
      }
      return (
        canApproveAllTasks ||
        Boolean(
          currentUserId && (quoteReviewerUserIds ?? []).includes(currentUserId),
        )
      );
    };
    const byId = new Map<string, ThreadSummaryQuote>();
    if (opportunityId) {
      for (const quote of quotesById.values()) {
        if (quote.opportunityId !== opportunityId) {
          continue;
        }
        const isOpenDraft =
          quote.status === QuoteStatusEnum.DRAFT &&
          !quote.archivedAt &&
          quote.isLatestRevision;
        const status = pendingTasksByQuoteId.has(quote.id)
          ? ThreadSummaryQuoteStatus.AWAITING_APPROVAL
          : quote.status === QuoteStatusEnum.FINALIZED
            ? ThreadSummaryQuoteStatus.FINALIZED
            : isOpenDraft
              ? ThreadSummaryQuoteStatus.DRAFT
              : ThreadSummaryQuoteStatus.OTHER;
        byId.set(quote.id, {
          quoteId: quote.id,
          label: quote.name ?? "Quote",
          displayNumber: `#${quote.quoteDisplayNumber}${quote.revision > 1 ? `-R${quote.revision}` : ""}`,
          status,
          canReview: canReviewQuote(quote.id, status),
        });
      }
    }
    for (const task of openTasks) {
      const quoteId =
        task.actionParameters &&
        task.actionParameters.actionId !== TASK_ACTION_ID.SEND_EMAIL
          ? task.actionParameters.parameters.quoteId
          : undefined;
      if (quoteId && !byId.has(quoteId)) {
        const status = pendingTasksByQuoteId.has(quoteId)
          ? ThreadSummaryQuoteStatus.AWAITING_APPROVAL
          : ThreadSummaryQuoteStatus.DRAFT;
        byId.set(quoteId, {
          quoteId,
          label: task.subject ?? "Quote",
          status,
          canReview: canReviewQuote(quoteId, status),
        });
      }
    }
    return Array.from(byId.values());
  }, [
    opportunityId,
    quotesById,
    openTasks,
    currentUser,
    currentUserId,
    canApproveAllTasks,
    quoteReviewerUserIds,
  ]);

  const hasSummaryContent =
    Boolean(opportunityId && opportunity?.name) ||
    summaryQuotes.length > 0 ||
    summaryAssets.length > 0;

  const threadWaitTimers = useMemo(
    () => (threadId ? (waitTimersByThread.get(threadId) ?? []) : []),
    [waitTimersByThread, threadId],
  );
  const [dismissedTaskIds, setDismissedTaskIds] = useState<Set<string>>(
    new Set(),
  );
  const panelOpen = panelSelection.type !== "none";
  // Mirror of panelSelection for reads inside the turn-completion effect without
  // adding it as a dependency (which would defeat the status-transition guard).
  const panelSelectionRef = useRef(panelSelection);
  panelSelectionRef.current = panelSelection;
  // Generation counter for quote review requests: bumped on every new request
  // and whenever the sheet opens, closes, or switches threads, so an awaited
  // result that is no longer the latest is dropped.
  const quoteReviewRequestRef = useRef(0);
  const selectedAgentTaskId =
    panelSelection.type === "task" ? panelSelection.taskId : null;
  const selectedAgentTask = useMemo(
    () => openTasks.find((task) => task.id === selectedAgentTaskId) ?? null,
    [openTasks, selectedAgentTaskId],
  );
  const { openLinkedTask } = useTaskDeepLink({
    enabled: enableTaskDeepLink,
    selectedTaskId: selectedAgentTaskId,
  });
  // openTasks is replaced wholesale on every live-refresh poll, so a resolved
  // task — approved elsewhere, or consumed by the agent — drops out from under
  // its open sheet. Close it: the task is settled and nothing live is left to
  // act on. The guards fire only on the transition "this same task was open
  // and is now gone", so an unloaded task list or a panel switch closes
  // nothing. The turn-completion pass re-picks any replacement task.
  const prevSelectedOpenTaskIdRef = useRef<string | null>(null);
  useEffect(() => {
    const prevOpenId = prevSelectedOpenTaskIdRef.current;
    prevSelectedOpenTaskIdRef.current = selectedAgentTask?.id ?? null;
    if (
      prevOpenId === null ||
      selectedAgentTask !== null ||
      selectedAgentTaskId !== prevOpenId
    ) {
      return;
    }
    setPanelSelection((prev) =>
      prev.type === "task" && prev.taskId === prevOpenId
        ? { type: "none" }
        : prev,
    );
  }, [selectedAgentTask, selectedAgentTaskId]);
  const richActionMessageId =
    panelSelection.type === "richAction" ? panelSelection.messageId : null;
  const isMessagesLoading = threadId
    ? (isLoadingMessages.get(threadId) ?? false)
    : false;

  const refreshKey = useMemo(
    () => (thread?.updatedAt ? new Date(thread.updatedAt).getTime() : 0),
    [thread?.updatedAt],
  );

  const prevThreadStatusRef = useRef(thread?.status);

  const messageTree = useMemo(() => buildMessageTree(messages), [messages]);

  const messagesById = useMemo(() => {
    const map = new Map<string, AgentSdkMessage>();
    for (const msg of messages) {
      map.set(msg.id, msg);
    }
    return map;
  }, [messages]);

  const activeWaitTimer = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const timer = messages[i].waitTimer;
      if (timer?.status === AGENT_WORKFLOW_RUN_WAIT_TIMER_STATUS.WAITING) {
        return timer;
      }
    }
    return null;
  }, [messages]);

  const isWaiting =
    thread?.status === AGENT_SDK_THREAD_STATUS.INTERRUPTED &&
    activeWaitTimer !== null;

  const activePendingTask = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const task = messages[i].task;
      if (task?.status === "PENDING_REVIEW") {
        return task;
      }
    }
    return null;
  }, [messages]);

  const waitingDescription = activePendingTask?.description ?? null;

  const waitingHeading = useMemo(() => {
    if (!activePendingTask?.assignees?.length) {
      return `${standardAgentName} is waiting...`;
    }

    const isCurrentUserAssignee = activePendingTask.assignees.some(
      (a) => a.assigneeUserId === currentUserId,
    );
    if (isCurrentUserAssignee) {
      return "Waiting for your input";
    }

    const assigneeNames = activePendingTask.assignees
      .map((a) =>
        a.firstName || a.lastName
          ? `${a.firstName ?? ""} ${a.lastName ?? ""}`.trim()
          : a.email,
      )
      .filter(Boolean);
    return assigneeNames.length > 0
      ? `Waiting on input from ${assigneeNames.join(", ")}`
      : `${standardAgentName} is waiting...`;
  }, [activePendingTask, currentUserId, standardAgentName]);

  useEffect(() => {
    if (!threadId || !isOpen) {
      return;
    }

    let isCancelled = false;

    const loadThread = async () => {
      await Promise.all([
        fetchThread(threadId),
        fetchMessages(threadId),
        fetchThreadVisibility(threadId),
      ]);

      if (isCancelled) {
        return;
      }

      const latestThread = useStandardAgentStore
        .getState()
        .threadMap.get(threadId);
      if (latestThread?.status === AGENT_SDK_THREAD_STATUS.ACTIVE) {
        pollUntilCompletion(threadId);
      } else {
        stopPolling(threadId);
      }
    };

    loadThread();

    return () => {
      isCancelled = true;
      stopPolling(threadId);
    };
  }, [
    threadId,
    isOpen,
    fetchThread,
    fetchMessages,
    pollUntilCompletion,
    stopPolling,
    fetchThreadVisibility,
  ]);

  // An idle thread has no other live signal: a task approved in another tab,
  // or a turn started elsewhere, is only seen when this tab polls. The thread
  // fetch carries the status; the message poll carries messages and open
  // tasks. A tick that finds the thread ACTIVE hands over to the fast poll.
  const isThreadActive = thread?.status === AGENT_SDK_THREAD_STATUS.ACTIVE;
  useVisiblePoll(
    async () => {
      if (!threadId) {
        return;
      }
      await fetchThread(threadId);
      await pollMessages(threadId);
      const latestThread = useStandardAgentStore
        .getState()
        .threadMap.get(threadId);
      if (latestThread?.status === AGENT_SDK_THREAD_STATUS.ACTIVE) {
        pollUntilCompletion(threadId);
      }
    },
    IDLE_THREAD_POLL_MS,
    Boolean(threadId && isOpen && !isThreadActive),
  );

  useEffect(() => {
    if (!isEditingThreadName) {
      setThreadNameInput(threadTitle);
    }
  }, [isEditingThreadName, threadTitle]);

  useEffect(() => {
    setIsEditingThreadName(false);
    setIsSavingThreadName(false);
    setThreadNameInput(threadTitle);
  }, [threadId, threadTitle]);

  const handleStartThreadNameEdit = useCallback(() => {
    setThreadNameInput(threadTitle);
    setIsEditingThreadName(true);
  }, [threadTitle]);

  const handleCancelThreadNameEdit = useCallback(() => {
    setThreadNameInput(threadTitle);
    setIsEditingThreadName(false);
  }, [threadTitle]);

  const handleSaveThreadName = useCallback(async () => {
    if (!threadId || isSavingThreadName) {
      return;
    }

    const trimmedName = threadNameInput.trim();
    if (!trimmedName) {
      toast.error("Thread name is required");
      return;
    }

    if (trimmedName === (thread?.name ?? "").trim()) {
      setThreadNameInput(threadTitle);
      setIsEditingThreadName(false);
      return;
    }

    setIsSavingThreadName(true);
    const success = await updateThreadName(threadId, trimmedName);
    setIsSavingThreadName(false);

    if (success) {
      setThreadNameInput(trimmedName);
      setIsEditingThreadName(false);
      return;
    }

    toast.error("Failed to rename thread");
  }, [
    isSavingThreadName,
    thread?.name,
    threadId,
    threadNameInput,
    threadTitle,
    updateThreadName,
  ]);

  const handleThreadNameKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        handleSaveThreadName();
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        handleCancelThreadNameEdit();
      }
    },
    [handleCancelThreadNameEdit, handleSaveThreadName],
  );

  /**
   * Fetches the next older page when the viewport sits near the top and more
   * history exists. Reads the cursor meta imperatively so callers stay stable
   * and never fire against a stale snapshot. Captures the first stream item and
   * its offset (plus scrollHeight as fallback) so the prepend can be anchored.
   */
  const maybeLoadOlder = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el || !threadId || el.scrollTop >= LOAD_OLDER_THRESHOLD_PX) {
      return;
    }
    const meta = useStandardAgentStore
      .getState()
      .messagesMetaByThread.get(threadId);
    if (!meta?.hasOlder || meta.isLoadingOlder) {
      return;
    }
    const first = messagesListRef.current?.firstElementChild ?? null;
    // Content-space offset (viewport offset + scrollTop), so scrolling that
    // happens while the fetch is in flight isn't undone by the restore.
    scrollAnchorRef.current = {
      node: first,
      top: first
        ? first.getBoundingClientRect().top -
          el.getBoundingClientRect().top +
          el.scrollTop
        : 0,
      scrollHeight: el.scrollHeight,
    };
    useStandardAgentStore.getState().fetchOlderMessages(threadId);
  }, [threadId]);

  /**
   * Loads older history near the top and tracks whether the list is at (or
   * near) the bottom so the view only auto-follows new output when the user
   * hasn't scrolled up. Our own programmatic scrolls are ignored so they can't
   * trigger loads or flip the follow flag mid-animation.
   */
  const handleMessagesScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el || isProgrammaticScrollRef.current) {
      return;
    }
    maybeLoadOlder();
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isAtBottomRef.current = distanceFromBottom < 80;
  }, [maybeLoadOlder]);

  /**
   * Registers that the user has taken over scrolling (wheel/touch) by cancelling
   * the programmatic-scroll guard, so an intentional scroll-up is honoured even
   * while the view is auto-following.
   */
  const handleUserScrollIntent = useCallback(() => {
    isProgrammaticScrollRef.current = false;
  }, []);

  /**
   * Wheel-up at the very top emits no scroll events (there is nothing left to
   * scroll), so it triggers the older-history load directly.
   */
  const handleMessagesWheel = useCallback(
    (event: React.WheelEvent) => {
      handleUserScrollIntent();
      if (event.deltaY < 0) {
        maybeLoadOlder();
      }
    },
    [handleUserScrollIntent, maybeLoadOlder],
  );

  /**
   * Clears the programmatic-scroll guard once the browser reports scrolling has
   * fully settled.
   */
  const handleScrollEnd = useCallback(() => {
    isProgrammaticScrollRef.current = false;
    if (programmaticScrollResetRef.current) {
      clearTimeout(programmaticScrollResetRef.current);
      programmaticScrollResetRef.current = null;
    }
  }, []);

  /**
   * Scrolls the message list to the bottom, flagging the scroll as programmatic
   * until the browser's scrollend fires (with a timeout fallback) so the scroll
   * handler doesn't mistake the resulting movement for the user scrolling away.
   * @param smooth - Whether to animate the scroll (true) or jump instantly (false)
   */
  const scrollToBottom = useCallback((smooth: boolean) => {
    const el = scrollContainerRef.current;
    if (!el) {
      return;
    }
    isProgrammaticScrollRef.current = true;
    if (programmaticScrollResetRef.current) {
      clearTimeout(programmaticScrollResetRef.current);
    }
    programmaticScrollResetRef.current = setTimeout(
      () => {
        isProgrammaticScrollRef.current = false;
      },
      smooth ? 700 : 100,
    );
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  }, []);

  /**
   * Attaches the scroll container via a callback ref, wiring up a ResizeObserver
   * (so the view re-pins when bars below it shrink the scroll area) and a
   * scrollend listener. A callback ref guarantees these attach exactly when the
   * element mounts, regardless of the sheet's open/close lifecycle.
   * @param node - The scroll container element, or null on unmount
   */
  const setScrollContainer = useCallback(
    (node: HTMLDivElement | null) => {
      const previous = scrollContainerRef.current;
      if (previous) {
        resizeObserverRef.current?.unobserve(previous);
        previous.removeEventListener("scrollend", handleScrollEnd);
      }
      scrollContainerRef.current = node;
      if (!node) {
        return;
      }
      if (!resizeObserverRef.current) {
        resizeObserverRef.current = new ResizeObserver(() => {
          if (isAtBottomRef.current) {
            scrollToBottom(false);
          }
        });
      }
      resizeObserverRef.current.observe(node);
      node.addEventListener("scrollend", handleScrollEnd);
    },
    [handleScrollEnd, scrollToBottom],
  );

  useEffect(
    () => () => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      if (programmaticScrollResetRef.current) {
        clearTimeout(programmaticScrollResetRef.current);
      }
    },
    [],
  );

  // Reset the anchoring cursors when switching threads so a smaller oldest
  // sequence in the next thread isn't mistaken for a prepend.
  useLayoutEffect(() => {
    prevOldestSequenceRef.current = null;
    scrollAnchorRef.current = null;
  }, [threadId]);

  // Preserve the viewport when older history is prepended: a drop in the oldest
  // loaded sequence means the list grew upward, so shift scrollTop by how far
  // the anchored item moved (or by the height delta if it was remounted),
  // keeping the message the user was reading in place.
  useLayoutEffect(() => {
    const oldest = messagesMeta?.oldestSequence ?? null;
    const prevOldest = prevOldestSequenceRef.current;
    prevOldestSequenceRef.current = oldest;

    const anchor = scrollAnchorRef.current;
    if (
      prevOldest === null ||
      oldest === null ||
      oldest >= prevOldest ||
      anchor === null
    ) {
      return;
    }

    const el = scrollContainerRef.current;
    if (el) {
      if (anchor.node?.isConnected) {
        const newTop =
          anchor.node.getBoundingClientRect().top -
          el.getBoundingClientRect().top +
          el.scrollTop;
        el.scrollTop += newTop - anchor.top;
      } else {
        el.scrollTop += el.scrollHeight - anchor.scrollHeight;
      }
    }
    scrollAnchorRef.current = null;
  }, [messages, messagesMeta?.oldestSequence]);

  // Follow new messages while pinned to the bottom (smooth).
  useEffect(() => {
    if (isAtBottomRef.current) {
      scrollToBottom(true);
    }
  }, [messages, scrollToBottom]);

  // The task list, wait-timer, and status bars render below the scroll area and
  // shrink it the moment they appear. They live in separate store slices from
  // `messages`, so the effect above doesn't react to them. Re-pin synchronously
  // before paint so the correction lands in the same frame, with no visible
  // offset. The ResizeObserver on the container is the backstop for any other
  // viewport change (window resize, composer growth, panel collapse).
  useLayoutEffect(() => {
    if (isAtBottomRef.current) {
      scrollToBottom(false);
    }
  }, [openTasks, threadWaitTimers, thread?.status, scrollToBottom]);

  const clearPanelSelection = useCallback(() => {
    setPanelSelection({ type: "none" });
  }, []);

  const dismissSelectedTask = useCallback(() => {
    if (panelSelection.type === "task") {
      setDismissedTaskIds((prev) => {
        const next = new Set(prev);
        next.add(panelSelection.taskId);
        return next;
      });
    }
    clearPanelSelection();
  }, [clearPanelSelection, panelSelection]);

  const openRichActionPanelForMessage = useCallback((messageId: string) => {
    setPanelSelection({ type: "richAction", messageId });
  }, []);

  const openAgentTaskPanel = useCallback((taskId: string) => {
    setPanelSelection({ type: "task", taskId });
  }, []);

  const openQuoteReview = useCallback(
    async (quoteId: string) => {
      const canReview = summaryQuotes.some(
        (quote) => quote.quoteId === quoteId && quote.canReview,
      );
      const request = quoteReviewRequestRef.current + 1;
      quoteReviewRequestRef.current = request;
      if (!threadId || !canReview) {
        // Quotes the thread cannot open for review (already finalized, or the
        // user is not an approver) still get the read-only preview.
        setOpeningQuoteId(null);
        setQuotePreviewId(quoteId);
        return;
      }
      setOpeningQuoteId(quoteId);
      const isConfiguredReviewer = Boolean(
        currentUserId && (quoteReviewerUserIds ?? []).includes(currentUserId),
      );
      const taskId = await createQuoteReviewTask(
        threadId,
        quoteId,
        isConfiguredReviewer
          ? TASK_ACTION_ID.FINALIZE_QUOTE
          : TASK_ACTION_ID.PREVIEW_QUOTE,
      );
      if (quoteReviewRequestRef.current !== request) {
        return;
      }
      setOpeningQuoteId(null);
      if (taskId) {
        openAgentTaskPanel(taskId);
        return;
      }
      setQuotePreviewId(quoteId);
    },
    [
      summaryQuotes,
      openAgentTaskPanel,
      threadId,
      createQuoteReviewTask,
      currentUserId,
      quoteReviewerUserIds,
    ],
  );

  const handleTaskClick = useCallback(
    (task: AgentTask) => {
      openAgentTaskPanel(task.id);
    },
    [openAgentTaskPanel],
  );

  const pendingScreeningMessage = useMemo(
    () => findPendingScreeningMessage(messages),
    [messages],
  );

  const latestMessageCreatedAtMs = useMemo(() => {
    if (messages.length === 0) {
      return null;
    }
    return messages.reduce(
      (latest, message) =>
        Math.max(latest, new Date(message.createdAt).getTime()),
      0,
    );
  }, [messages]);

  const maybeAutoOpenPanel = useCallback(
    (options?: {
      ignoreCurrentSelection?: boolean;
      dismissedIds?: Set<string>;
      richActionCreatedAfterMs?: number;
    }) => {
      if (!options?.ignoreCurrentSelection && panelSelection.type !== "none") {
        return;
      }
      if (!currentUserId) {
        return;
      }

      const dismissedIds = options?.dismissedIds ?? dismissedTaskIds;
      const userOwnedTasks = openTasks
        .filter(
          (task) =>
            task.status === AgentTaskStatus.IN_PROGRESS &&
            task.type === AgentWorkflowRunTaskType.ACTION &&
            !dismissedIds.has(task.id) &&
            task.owners.some(
              (owner) => owner.type === "userId" && owner.id === currentUserId,
            ),
        )
        .sort((a, b) => {
          const actionPriority =
            Number(Boolean(b.actionParameters)) -
            Number(Boolean(a.actionParameters));
          if (actionPriority !== 0) {
            return actionPriority;
          }
          return (
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          );
        });
      if (userOwnedTasks.length > 0) {
        openAgentTaskPanel(userOwnedTasks[0].id);
        return;
      }

      const candidateMessages =
        options?.richActionCreatedAfterMs === undefined
          ? messages
          : messages.filter(
              (msg) =>
                new Date(msg.createdAt).getTime() >
                options.richActionCreatedAfterMs!,
            );

      const pendingScreeningCandidate =
        findPendingScreeningMessage(candidateMessages);

      const richActionMessages = candidateMessages.filter((msg) => {
        if (msg.message.type !== "tool_call") {
          return false;
        }
        return getRichActionType(msg.message) !== undefined;
      });

      const mostRecent = pendingScreeningCandidate
        ? pendingScreeningCandidate
        : richActionMessages.length > 0
          ? richActionMessages[richActionMessages.length - 1]
          : undefined;

      if (!mostRecent) {
        return;
      }

      const mostRecentMsg = mostRecent.message;
      if (mostRecentMsg.type === "tool_call") {
        const callId = mostRecentMsg.call_id;
        const resultMsg = messages.find(
          (m) =>
            m.message.type === "tool_call_result" &&
            m.message.call_id === callId,
        );
        if (extractToolCallError(resultMsg)) {
          if (richActionMessageId === mostRecent.id && panelOpen) {
            clearPanelSelection();
          }
          return;
        }
      }

      const userCanApprove = canUserApproveTask(
        currentUser,
        mostRecent.task,
        canApproveAllTasks,
      );
      if (userCanApprove) {
        openRichActionPanelForMessage(mostRecent.id);
      }
    },
    [
      canApproveAllTasks,
      clearPanelSelection,
      currentUser,
      currentUserId,
      dismissedTaskIds,
      messages,
      openAgentTaskPanel,
      openTasks,
      openRichActionPanelForMessage,
      panelOpen,
      panelSelection,
      richActionMessageId,
    ],
  );

  useEffect(() => {
    hasTriedInitialAutoOpenRef.current = false;
    shouldWaitForFreshInitialLoadRef.current = Boolean(threadId && isOpen);
    hasObservedFreshInitialLoadRef.current = false;
    activeTurnStartLatestMessageMsRef.current = null;
    isAtBottomRef.current = true;
    quoteReviewRequestRef.current += 1;
    setOpeningQuoteId(null);
    setDismissedTaskIds(new Set());
    clearPanelSelection();
  }, [clearPanelSelection, isOpen, threadId]);

  useEffect(() => {
    if (
      !threadId ||
      !isOpen ||
      hasTriedInitialAutoOpenRef.current ||
      !currentUserId
    ) {
      return;
    }

    if (isMessagesLoading) {
      hasObservedFreshInitialLoadRef.current = true;
      return;
    }

    if (
      shouldWaitForFreshInitialLoadRef.current &&
      !hasObservedFreshInitialLoadRef.current
    ) {
      return;
    }

    hasTriedInitialAutoOpenRef.current = true;
    if (openLinkedTask(openTasks, openAgentTaskPanel)) {
      return;
    }
    maybeAutoOpenPanel();
  }, [
    threadId,
    isOpen,
    isMessagesLoading,
    currentUserId,
    maybeAutoOpenPanel,
    openLinkedTask,
    openTasks,
    openAgentTaskPanel,
  ]);

  useEffect(() => {
    if (
      !isOpen ||
      !threadId ||
      isMessagesLoading ||
      thread?.status !== AGENT_SDK_THREAD_STATUS.ACTIVE ||
      activeTurnStartLatestMessageMsRef.current !== null
    ) {
      return;
    }

    activeTurnStartLatestMessageMsRef.current = latestMessageCreatedAtMs;
  }, [
    isOpen,
    isMessagesLoading,
    latestMessageCreatedAtMs,
    thread?.status,
    threadId,
  ]);

  useEffect(() => {
    const prev = prevThreadStatusRef.current;
    const curr = thread?.status;
    prevThreadStatusRef.current = curr;

    if (curr === AGENT_SDK_THREAD_STATUS.ACTIVE && prev !== curr) {
      activeTurnStartLatestMessageMsRef.current = latestMessageCreatedAtMs;
      replacementCheckTasksRef.current = null;
      return;
    }

    if (
      prev === AGENT_SDK_THREAD_STATUS.ACTIVE &&
      curr &&
      curr !== AGENT_SDK_THREAD_STATUS.ACTIVE
    ) {
      // Skip when closed; the open effect refetches on the next open.
      if (isOpen && opportunityId) {
        fetchQuotes({ opportunityId });
      }
      // The final message fetch lands one commit after this status write, so
      // openTasks/messages here can be one tick stale; the re-check effect
      // below retries whatever this pass misses once they refresh.
      replacementCheckTasksRef.current = openTasks;
      const selection = panelSelectionRef.current;
      if (selection.type === "none") {
        const resetDismissedIds = new Set<string>();
        setDismissedTaskIds(resetDismissedIds);
        setPanelSelection({ type: "none" });
        maybeAutoOpenPanel({
          ignoreCurrentSelection: true,
          dismissedIds: resetDismissedIds,
          richActionCreatedAfterMs:
            activeTurnStartLatestMessageMsRef.current ?? undefined,
        });
        return;
      }
      // Keep the user's open panel: a rich-action view refreshes in place, and
      // a task sheet whose task the turn consumed closes via the vanish effect
      // above — the armed re-check below then re-picks its replacement.
    }
  }, [
    isOpen,
    latestMessageCreatedAtMs,
    maybeAutoOpenPanel,
    openTasks,
    thread?.status,
    opportunityId,
    fetchQuotes,
  ]);

  // Retries the completion pass (auto-open, or follow-to-replacement) when
  // the transition saw stale data: acts once, on the first openTasks refresh
  // after arming. A live task or rich-action selection is left alone.
  useEffect(() => {
    const armedAgainst = replacementCheckTasksRef.current;
    if (!armedAgainst || openTasks === armedAgainst) {
      return;
    }
    replacementCheckTasksRef.current = null;
    if (thread?.status === AGENT_SDK_THREAD_STATUS.ACTIVE) {
      return;
    }
    const selection = panelSelectionRef.current;
    if (selection.type === "richAction") {
      return;
    }
    if (
      selection.type === "task" &&
      openTasks.some((task) => task.id === selection.taskId)
    ) {
      return;
    }
    maybeAutoOpenPanel({
      ignoreCurrentSelection: true,
      richActionCreatedAfterMs:
        activeTurnStartLatestMessageMsRef.current ?? undefined,
    });
  }, [openTasks, thread?.status, maybeAutoOpenPanel]);

  /**
   * Sends the reply message to the thread.
   */
  const handleSendReply = useCallback(
    async (text: string): Promise<boolean> => {
      const trimmed = text.trim();
      if (!threadId || !trimmed || isSending || isUploadingFile) {
        return false;
      }

      const assetIds =
        attachedAssets.length > 0
          ? attachedAssets.map((asset) => asset.id)
          : [];
      // Snapshot (not subscribe): the view at the moment of send is what the
      // agent should see stamped on this message.
      const viewContext = useViewContextStore.getState().view ?? undefined;
      const result = await addMessage(
        threadId,
        trimmed,
        assetIds.length > 0 ? assetIds : undefined,
        viewContext,
      );
      if (result) {
        isAtBottomRef.current = true;
        setAttachedAssets([]);
        // Deliberately keep the panel selection: sending a message must not
        // close an open quote/item editor. Its contents live-refresh in place
        // and the header actions are hidden while the agent works.
        return true;
      }
      toast.error("Failed to send message");
      return false;
    },
    [threadId, isSending, isUploadingFile, attachedAssets, addMessage],
  );

  /**
   * Uploads an array of files to asset storage and adds them to attachedAssets.
   * @param files - The files to upload
   */
  const uploadFiles = async (files: File[]) => {
    if (files.length === 0) {
      return;
    }

    setIsUploadingFile(true);

    for (const file of files) {
      try {
        const response = await fetchApi("api/assets/upload", "POST", {
          body: {
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
          },
        });

        if (!response.ok || !response.data) {
          continue;
        }

        const parsed = UploadResponseSchema.safeParse(response.data);
        if (!parsed.success) {
          continue;
        }

        const data = parsed.data;

        await fetch(data.uploadUrl, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type,
          },
        });

        setAttachedAssets((prev) => [...prev, mapUploadedAsset(data.asset)]);
      } catch (error) {
        const uploadError =
          error instanceof Error ? error : new Error("Failed to upload file");
        captureException(uploadError);
      }
    }

    setIsUploadingFile(false);
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }
    await uploadFiles(Array.from(files));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /**
   * Handles files dropped onto the reply input area.
   * @param event - The drag event from the drop zone
   */
  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    if (
      isSending ||
      isUploadingFile ||
      thread?.status === AGENT_SDK_THREAD_STATUS.ACTIVE ||
      pendingScreeningMessage !== undefined
    ) {
      return;
    }

    const files = Array.from(event.dataTransfer.files);
    await uploadFiles(files);
  };

  const handleRemoveAsset = (assetId: string) => {
    setAttachedAssets((prev) => prev.filter((asset) => asset.id !== assetId));
  };

  const toggleSection = useCallback((id: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleApprove = useCallback(
    async (taskId: string) => {
      if (!threadId) {
        return;
      }
      const success = await approveTask(taskId, threadId);
      if (!success) {
        return;
      }
      toast.success("Task completed");
      dismissSelectedTask();
      // Approval runs the task action inline, so a finalized quote is already
      // stale in the quotes store by the time the request resolves.
      if (opportunityId) {
        fetchQuotes({ opportunityId });
      }
    },
    [approveTask, threadId, dismissSelectedTask, opportunityId, fetchQuotes],
  );

  const handleDeny = useCallback((taskId: string) => {
    setCancelTaskId(taskId);
  }, []);

  const confirmDeny = useCallback(async () => {
    if (!threadId || !cancelTaskId) {
      return;
    }
    const success = await denyTask(cancelTaskId, threadId);
    setCancelTaskId(null);
    if (success) {
      toast.success("Task canceled");
      dismissSelectedTask();
    } else {
      toast.error("Could not cancel task. Please try again.");
    }
  }, [denyTask, threadId, cancelTaskId, dismissSelectedTask]);

  const handleRetry = useCallback(async () => {
    if (!threadId) {
      return;
    }
    const success = await retryThread(threadId);
    if (success) {
      clearPanelSelection();
    }
  }, [retryThread, threadId, clearPanelSelection]);

  const handleStopThread = useCallback(async () => {
    if (!threadId) {
      return;
    }
    const success = await stopThread(threadId);
    if (!success) {
      toast.error("Failed to stop the agent");
    }
  }, [stopThread, threadId]);

  const handleCancelWaitTimer = useCallback(async () => {
    if (!threadId) {
      return;
    }
    setIsCancellingTimer(true);
    const success = await cancelWaitTimer(threadId);
    setIsCancellingTimer(false);
    if (!success) {
      toast.error("Failed to cancel wait timer");
    }
  }, [cancelWaitTimer, threadId]);

  const handleOnClose = useCallback(() => {
    clearPanelSelection();
    onClose();
  }, [onClose, clearPanelSelection]);

  const handleSaveOpportunity = useCallback(
    async (data: UpdateOpportunityParams): Promise<true | string> => {
      if (!opportunityId) {
        return "No opportunity selected";
      }
      const { opportunity: updated, error } = await updateOpportunity(
        opportunityId,
        data,
      );
      return updated ? true : (error ?? "Failed to update opportunity");
    },
    [opportunityId, updateOpportunity],
  );

  const handleArchiveOpportunity = useCallback(
    async (id: string) => {
      await archiveOpportunity(id);
    },
    [archiveOpportunity],
  );

  const handleUnarchiveOpportunity = useCallback(
    async (id: string) => {
      await unarchiveOpportunity(id);
    },
    [unarchiveOpportunity],
  );

  const richActionMessage = richActionMessageId
    ? messagesById.get(richActionMessageId)
    : undefined;

  const richActionData = useMemo(() => {
    if (!richActionMessage) {
      return null;
    }
    const toolCallMsg = richActionMessage.message;
    if (toolCallMsg.type !== "tool_call") {
      return mapSdkMessageToRichActionData(richActionMessage);
    }
    const resultMsg = messages.find(
      (m) =>
        m.message.type === "tool_call_result" &&
        m.message.call_id === toolCallMsg.call_id,
    );
    const error = extractToolCallError(resultMsg);
    return mapSdkMessageToRichActionData(richActionMessage, error);
  }, [richActionMessage, messages]);

  const streamItems = useMemo(
    () => groupIntoTurns(messageTree, isThreadActive),
    [messageTree, isThreadActive],
  );

  const timelineCtx = useMemo<TimelineStepContext>(
    () => ({
      expandedSections,
      onToggleSection: toggleSection,
      showCancelled,
      isThreadActive,
      onOpenRichAction: openRichActionPanelForMessage,
    }),
    [
      expandedSections,
      toggleSection,
      showCancelled,
      isThreadActive,
      openRichActionPanelForMessage,
    ],
  );

  const renderUserMessage = useCallback(
    (node: MessageNode): React.ReactNode => {
      const message = node.message;
      const senderInfo = getUserSenderInfo(message, currentUser, getUserById);
      const movedFrom = getMovedMessageSource(message, standardAgentName);
      const hasScreeningHit =
        message.task?.status === "PENDING_REVIEW" &&
        pendingScreeningMessage !== undefined;

      return (
        <div key={message.id} className="space-y-4">
          <WorkflowRunViewUserMessage
            id={message.id}
            senderName={senderInfo.name}
            senderProfilePictureUrl={senderInfo.profilePictureUrl}
            senderDetails={senderInfo.details}
            content={extractMessageContent(message.message)}
            assets={message.assets}
            messageMetadata={message.messageMetadata}
            timestamp={message.createdAt}
            movedFrom={movedFrom}
          />
          {hasScreeningHit && (
            <ScreeningReviewCard
              message={message}
              messages={messages}
              approvingTaskId={approvingTaskId}
              denyingTaskId={denyingTaskId}
              onOpenPanel={openRichActionPanelForMessage}
              onApprove={handleApprove}
              onDeny={handleDeny}
            />
          )}
        </div>
      );
    },
    [
      currentUser,
      getUserById,
      pendingScreeningMessage,
      messages,
      approvingTaskId,
      denyingTaskId,
      openRichActionPanelForMessage,
      handleApprove,
      handleDeny,
      standardAgentName,
    ],
  );

  const renderAgentReply = useCallback(
    (node: MessageNode): React.ReactNode => {
      const message = node.message;
      const msg = message.message;
      const isMainAgent = message.agentId === "standard-agent";
      const senderName = isMainAgent ? standardAgentName : message.agentId;

      if (isSendRichMessageToolCall(msg)) {
        return (
          <div key={message.id}>
            <RichMessageDisplay message={message} senderName={senderName} />
          </div>
        );
      }

      const content = extractMessageContent(msg);
      if (!content && (message.assets?.length ?? 0) === 0) {
        return null;
      }
      return (
        <div key={message.id}>
          <WorkflowRunViewAssistantMessage
            id={message.id}
            senderName={senderName}
            content={content}
            assets={message.assets}
            timestamp={message.createdAt}
          />
        </div>
      );
    },
    [standardAgentName],
  );

  const renderBannerAction = useCallback(
    (node: MessageNode): React.ReactNode => {
      const message = node.message;
      const richActionType = getRichActionType(message.message);
      if (!richActionType) {
        return null;
      }
      if (message.task?.status === "CANCELLED" && !showCancelled) {
        return null;
      }
      const richActionError = extractToolCallError(node.toolResult);
      const canOpen = canOpenRichActionPanel(
        currentUser,
        message.task,
        canApproveAllTasks,
        richActionType,
      );
      return (
        <RichActionToolCallCard
          key={message.id}
          message={message}
          richActionType={richActionType}
          onOpenRichActionPanel={
            canOpen ? openRichActionPanelForMessage : undefined
          }
          richActionPanelOpen={
            panelSelection.type === "richAction" &&
            panelSelection.messageId === message.id
          }
          onApprove={handleApprove}
          onDeny={handleDeny}
          isApproving={approvingTaskId === message.task?.id}
          isDenying={denyingTaskId === message.task?.id}
          error={richActionError}
        />
      );
    },
    [
      currentUser,
      canApproveAllTasks,
      showCancelled,
      openRichActionPanelForMessage,
      panelSelection,
      handleApprove,
      handleDeny,
      approvingTaskId,
      denyingTaskId,
    ],
  );

  /**
   * Resolves the current thread state into an icon, label and colour, or null
   * when there is no state worth surfacing.
   * @returns The status descriptor, or null
   */
  const resolveThreadStatus = () => {
    if (!thread) {
      return null;
    }
    if (thread.status === AGENT_SDK_THREAD_STATUS.ACTIVE) {
      return {
        Icon: Loader2,
        label: "Working",
        tint: "text-info",
        motion: "animate-spin",
      };
    }
    if (thread.status === AGENT_SDK_THREAD_STATUS.FAILED) {
      return {
        Icon: XCircle,
        label: "Failed",
        tint: "text-destructive",
        motion: "",
      };
    }
    if (thread.pendingTaskCount && thread.pendingTaskCount > 0) {
      return {
        Icon: Pause,
        label: "Paused",
        tint: "text-warning",
        motion: "",
      };
    }
    if (thread.hasActiveWaitTimer) {
      return {
        Icon: Clock,
        label: "Waiting",
        tint: "text-info",
        motion: "animate-pulse",
      };
    }
    if (thread.status === AGENT_SDK_THREAD_STATUS.INTERRUPTED) {
      return {
        Icon: Pause,
        label: "Paused",
        tint: "text-warning",
        motion: "",
      };
    }
    if (thread.status === AGENT_SDK_THREAD_STATUS.COMPLETED) {
      return {
        Icon: CheckCircle,
        label: "Completed",
        tint: "text-green-600 dark:text-green-500",
        motion: "",
      };
    }
    return null;
  };

  /**
   * Renders the thread status as a bare icon indicator sized to sit alongside
   * the header action buttons, with the label surfaced on hover.
   * @returns The status indicator, or null
   */
  const getStatusIndicator = () => {
    const status = resolveThreadStatus();
    if (!status) {
      return null;
    }
    const { Icon, label, tint, motion } = status;
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={cn("flex h-9 w-9 items-center justify-center", tint)}
              aria-label={`Status: ${label}`}
            >
              <Icon className={cn("h-4 w-4", motion)} />
            </span>
          </TooltipTrigger>
          <TooltipContent>{label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const applyCorrespondenceWidth = (width: number) => {
    const layoutWidth =
      threadLayoutRef.current?.getBoundingClientRect().width ?? 0;
    const clampedWidth = clampCorrespondenceWidth(width, layoutWidth);
    correspondenceWidthRef.current = clampedWidth;
    setCorrespondenceWidth(clampedWidth);
    return clampedWidth;
  };

  const updateCorrespondenceWidth = (clientX: number) => {
    const layoutBounds = threadLayoutRef.current?.getBoundingClientRect();
    if (!layoutBounds || layoutBounds.width === 0) {
      return;
    }

    const widthPercent =
      ((clientX - layoutBounds.left) / layoutBounds.width) * 100;
    applyCorrespondenceWidth(widthPercent);
  };

  const handlePanelResizeStart = (
    event: React.PointerEvent<HTMLDivElement>,
  ) => {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    isResizingPanelsRef.current = true;
    setIsResizingPanels(true);
    updateCorrespondenceWidth(event.clientX);
  };

  const handlePanelResizeMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isResizingPanelsRef.current) {
      return;
    }

    updateCorrespondenceWidth(event.clientX);
  };

  const handlePanelResizeEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    isResizingPanelsRef.current = false;
    setIsResizingPanels(false);
    storeCorrespondenceWidth(correspondenceWidthRef.current);
  };

  const handlePanelResizeKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    const widthByKey: Partial<Record<string, number>> = {
      ArrowLeft: correspondenceWidth - CORRESPONDENCE_RESIZE_STEP_PERCENT,
      ArrowRight: correspondenceWidth + CORRESPONDENCE_RESIZE_STEP_PERCENT,
      Home: MIN_CORRESPONDENCE_WIDTH_PERCENT,
      End: MAX_CORRESPONDENCE_WIDTH_PERCENT,
    };
    const nextWidth = widthByKey[event.key];
    if (nextWidth === undefined) {
      return;
    }

    event.preventDefault();
    storeCorrespondenceWidth(applyCorrespondenceWidth(nextWidth));
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && handleOnClose()}>
      <SheetContent className={cn("w-screen overflow-hidden p-0")}>
        {threadError ? (
          <div className="flex flex-col items-center justify-center h-full px-4 text-center">
            <ShieldAlert className="h-10 w-10 text-danger mb-4" />
            <p className="text-sm text-muted-foreground max-w-md">
              {threadError}
            </p>
          </div>
        ) : (
          <>
            <div
              ref={threadLayoutRef}
              className={cn(
                "flex h-full relative",
                isResizingPanels && "select-none cursor-col-resize",
              )}
            >
              <div
                id={correspondencePaneId}
                className={cn(
                  "relative flex flex-col overflow-hidden",
                  !isResizingPanels &&
                    "transition-[width] duration-300 ease-in-out",
                  isMobile
                    ? "w-full"
                    : isChatCollapsed && panelOpen
                      ? "w-0"
                      : panelOpen
                        ? "min-w-0 shrink-0"
                        : "w-full",
                )}
                style={
                  !isMobile && panelOpen && !isChatCollapsed
                    ? {
                        width: `${correspondenceWidth}%`,
                        // Holds the floor for the cases no drag reports:
                        // a stored width, and the window being resized.
                        maxWidth: CORRESPONDENCE_MAX_WIDTH_CSS,
                      }
                    : undefined
                }
              >
                <SheetHeader className="px-4 py-2.5 flex-shrink-0">
                  <div className="flex items-center justify-between gap-2 [container-type:inline-size]">
                    <BackButton className="shrink-0" onClick={handleOnClose} />
                    <SheetTitle className="group/title flex min-w-0 flex-1 items-center gap-1.5 text-sm font-medium">
                      {!thread ? (
                        <Skeleton className="h-4 w-32" />
                      ) : isEditingThreadName ? (
                        <div className="flex min-w-0 flex-1 items-center gap-1">
                          <Input
                            autoFocus
                            aria-label="Thread name"
                            className="h-7 min-w-0 flex-1 text-sm"
                            disabled={isSavingThreadName}
                            maxLength={255}
                            onChange={(event) =>
                              setThreadNameInput(event.target.value)
                            }
                            onFocus={(event) => event.currentTarget.select()}
                            onKeyDown={handleThreadNameKeyDown}
                            value={threadNameInput}
                          />
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  aria-label="Save thread name"
                                  className="h-8 w-8 shrink-0"
                                  disabled={isSavingThreadName}
                                  onClick={handleSaveThreadName}
                                  size="icon"
                                  variant="ghost"
                                >
                                  {isSavingThreadName ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Check className="h-4 w-4" />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Save name</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  aria-label="Cancel thread name edit"
                                  className="h-8 w-8 shrink-0"
                                  disabled={isSavingThreadName}
                                  onClick={handleCancelThreadNameEdit}
                                  size="icon"
                                  variant="ghost"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Cancel</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      ) : (
                        <>
                          <span
                            className="truncate text-muted-foreground"
                            title={threadTitle}
                          >
                            {threadTitle}
                          </span>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  aria-label="Edit thread name"
                                  className="h-6 w-6 shrink-0 text-muted-foreground opacity-0 transition-opacity focus-visible:opacity-100 group-hover/title:opacity-100"
                                  onClick={handleStartThreadNameEdit}
                                  size="icon"
                                  variant="ghost"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Edit name</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </>
                      )}
                    </SheetTitle>
                    <div className="flex items-center gap-0.5">
                      {getStatusIndicator()}
                      {hasCancelledTask && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs text-muted-foreground"
                          aria-label={
                            showCancelled ? "Hide cancelled" : "Show cancelled"
                          }
                          onClick={() => setShowCancelled((prev) => !prev)}
                        >
                          {showCancelled ? (
                            <EyeOff className="[@container(min-width:480px)]:hidden" />
                          ) : (
                            <Eye className="[@container(min-width:480px)]:hidden" />
                          )}
                          <span className="hidden [@container(min-width:480px)]:inline">
                            {showCancelled
                              ? "Hide cancelled"
                              : "Show cancelled"}
                          </span>
                        </Button>
                      )}
                      {thread &&
                        thread.userId &&
                        thread.visibility &&
                        threadId && (
                          <ThreadVisibilityDropdown
                            threadId={threadId}
                            visibility={thread.visibility}
                            isOwner={thread.userId === currentUserId}
                            ownerId={thread.userId}
                            visibilityData={threadVisibilityData}
                          />
                        )}
                      {threadId && (
                        <CopyLinkButton
                          path={`/standard-agent?threadId=${threadId}`}
                          label={threadTitle}
                          ariaLabel="Copy link to thread"
                        />
                      )}
                      {hasSummaryContent && !panelOpen && !isMobile && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() =>
                                  setIsSummaryOpen((prev) => !prev)
                                }
                                aria-label="Toggle summary"
                                aria-pressed={isSummaryOpen}
                                className={cn(
                                  "hidden transition-[color,transform] active:scale-90 motion-reduce:active:scale-100 xl:inline-flex",
                                  isSummaryOpen
                                    ? "text-foreground"
                                    : "text-muted-foreground",
                                )}
                              >
                                <PanelRight className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {isSummaryOpen ? "Hide summary" : "Show summary"}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </div>
                </SheetHeader>

                <div
                  ref={setScrollContainer}
                  onScroll={handleMessagesScroll}
                  onWheel={handleMessagesWheel}
                  onTouchMove={handleUserScrollIntent}
                  className="flex min-h-0 flex-1 overflow-y-auto [overflow-anchor:none]"
                >
                  <div
                    className={cn(
                      "h-fit min-w-0 flex-1 px-6 py-4 transition-[padding] duration-300 ease-in-out motion-reduce:transition-none",
                      isSummaryOpen &&
                        hasSummaryContent &&
                        !panelOpen &&
                        !isMobile &&
                        "2xl:pl-[23.5rem]",
                    )}
                  >
                    {messagesMeta?.isLoadingOlder && (
                      <div className="pointer-events-none sticky top-2 z-10 flex h-0 justify-center overflow-visible">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </div>
                    )}
                    <div
                      ref={messagesListRef}
                      className="mx-auto w-full max-w-3xl space-y-6"
                    >
                      {(isMessagesLoading ||
                        thread?.status === AGENT_SDK_THREAD_STATUS.ACTIVE) &&
                      messageTree.length === 0 ? (
                        <div className="space-y-4">
                          {[1, 2, 3].map((i) => (
                            <div key={i} className="flex items-start gap-3">
                              <Skeleton className="w-8 h-8 rounded-full" />
                              <div className="flex-1 space-y-2">
                                <Skeleton className="h-4 w-20" />
                                <Skeleton className="h-16 w-full" />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : messageTree.length === 0 ? (
                        <div className="text-center text-muted-foreground py-8">
                          No messages yet
                        </div>
                      ) : (
                        streamItems.map((item) => {
                          if (item.type === "compaction") {
                            return (
                              <div key={item.node.message.id} className="ml-11">
                                <div className="inline-flex items-center gap-2 rounded-md border border-muted bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                                  <Archive className="h-4 w-4" />
                                  <span>Conversation compacted</span>
                                </div>
                              </div>
                            );
                          }

                          const { turn } = item;
                          const stepCount = getVisibleStepCount(
                            turn,
                            showCancelled,
                          );
                          const durationMs = getTurnDurationMs(turn);
                          const sectionId = `turn-timeline-${turn.id}`;
                          const hasWork = stepCount > 0 || turn.isOpen;
                          const agentReply =
                            turn.finalMessage &&
                            renderAgentReply(turn.finalMessage);
                          return (
                            <React.Fragment key={turn.id}>
                              {(turn.userMessage || hasWork) && (
                                <div className="space-y-2">
                                  {turn.userMessage &&
                                    renderUserMessage(turn.userMessage)}
                                  {hasWork && (
                                    <AgentWorkTimeline
                                      agentName={standardAgentName}
                                      isActive={turn.isOpen}
                                      stepCount={stepCount}
                                      durationLabel={
                                        durationMs !== null
                                          ? formatTurnDuration(durationMs)
                                          : null
                                      }
                                      isExpanded={expandedSections.has(
                                        sectionId,
                                      )}
                                      onToggle={() => toggleSection(sectionId)}
                                    >
                                      <TimelineStepList
                                        nodes={turn.timelineItems}
                                        ctx={timelineCtx}
                                      />
                                    </AgentWorkTimeline>
                                  )}
                                </div>
                              )}
                              {turn.bannerActions.map((node) =>
                                renderBannerAction(node),
                              )}
                              {(agentReply ||
                                turn.movedMessages.length > 0) && (
                                <div className="space-y-3">
                                  {agentReply}
                                  {turn.movedMessages.map((node) => (
                                    <div
                                      key={node.message.id}
                                      className="ml-11"
                                    >
                                      <SendMessageToThreadToolCallCard
                                        node={node}
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </React.Fragment>
                          );
                        })
                      )}
                      {isWaiting && (
                        <div className="ml-11 rounded-lg border border-warning/30 border-l-2 border-l-warning/50 bg-warning/10 p-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                          <div className="flex items-start gap-3">
                            <span className="flex items-center justify-center h-6 w-6 rounded-full bg-warning/15 mt-0.5 flex-shrink-0">
                              <Clock className="h-3.5 w-3.5 text-warning animate-pulse" />
                            </span>
                            <div className="flex-1 space-y-1">
                              <p className="text-sm font-medium">
                                {waitingHeading}
                              </p>
                              {waitingDescription && (
                                <p className="text-sm text-muted-foreground">
                                  {waitingDescription}
                                </p>
                              )}
                              {activeWaitTimer.waitUntil && (
                                <p className="text-xs text-muted-foreground">
                                  Will auto-resume at{" "}
                                  {new Date(
                                    activeWaitTimer.waitUntil,
                                  ).toLocaleString(undefined, {
                                    dateStyle: "short",
                                    timeStyle: "short",
                                  })}
                                </p>
                              )}
                            </div>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    onClick={() =>
                                      setIsCancelTimerDialogOpen(true)
                                    }
                                    disabled={isCancellingTimer}
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7 flex-shrink-0 hover:bg-destructive/10 hover:text-destructive transition-colors"
                                  >
                                    {isCancellingTimer ? (
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                      <X className="h-3.5 w-3.5" />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Cancel wait timer</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {hasSummaryContent && !panelOpen && !isMobile && (
                    <aside
                      aria-hidden={!isSummaryOpen}
                      className={cn(
                        "sticky top-0 hidden h-full shrink-0 border-l py-4 transition-all duration-300 ease-in-out motion-reduce:transition-none xl:block 2xl:border-l-0",
                        isSummaryOpen
                          ? "w-[22rem] overflow-y-auto overflow-x-hidden opacity-100"
                          : "w-0 overflow-hidden opacity-0",
                      )}
                    >
                      <div className="w-[22rem] pl-5 pr-4">
                        <ThreadSummaryPanel
                          opportunityId={opportunityId}
                          opportunityName={opportunity?.name ?? null}
                          isPrimaryThread={
                            !!threadId &&
                            opportunity?.primaryThreadId === threadId
                          }
                          assets={summaryAssets}
                          quotes={summaryQuotes}
                          onOpportunityClick={() =>
                            setIsOpportunitySheetOpen(true)
                          }
                          onAssetClick={(asset) =>
                            setSummaryAssetPreview({
                              id: asset.id,
                              fileName: asset.fileName,
                            })
                          }
                          onQuoteClick={openQuoteReview}
                          openingQuoteId={openingQuoteId}
                        />
                      </div>
                    </aside>
                  )}
                </div>

                {threadId && openTasks.length > 0 && (
                  <ThreadTaskList
                    key={threadId}
                    groups={taskGroups}
                    currentUserId={currentUserId}
                    onTaskClick={handleTaskClick}
                    selectedTaskId={selectedAgentTaskId}
                  />
                )}
                {threadWaitTimers.length > 0 && (
                  <ThreadWaitTimerBar timers={threadWaitTimers} />
                )}

                {thread?.status === AGENT_SDK_THREAD_STATUS.ACTIVE && (
                  <div className="border-t px-6 py-2.5 text-xs text-muted-foreground animate-in fade-in slide-in-from-bottom-1 duration-200 bg-info/10">
                    <div className="mx-auto w-full max-w-3xl flex items-center gap-2">
                      <ManufacturingSpinner />
                    </div>
                  </div>
                )}
                {isWaiting && (
                  <div className="border-t px-6 py-3 animate-in fade-in slide-in-from-bottom-1 duration-200 bg-warning/10">
                    <p className="mx-auto w-full max-w-3xl text-sm text-muted-foreground flex items-center gap-2">
                      <Send className="h-3.5 w-3.5 text-warning" />
                      Send a message to resume immediately.
                    </p>
                  </div>
                )}
                {thread?.status === AGENT_SDK_THREAD_STATUS.INTERRUPTED &&
                  !isWaiting && (
                    <div className="border-t px-6 py-2.5 text-xs text-muted-foreground animate-in fade-in slide-in-from-bottom-1 duration-200 bg-warning/10">
                      <div className="mx-auto w-full max-w-3xl flex items-center gap-3">
                        <span className="flex items-center justify-center h-5 w-5 rounded-full bg-warning/15 shrink-0">
                          <Pause className="h-2.5 w-2.5 text-warning" />
                        </span>
                        <span>
                          {standardAgentName} is paused. Send a message to
                          resume.
                        </span>
                      </div>
                    </div>
                  )}
                {thread?.status === AGENT_SDK_THREAD_STATUS.FAILED && (
                  <div className="border-t px-6 py-3 animate-in fade-in slide-in-from-bottom-1 duration-200 bg-danger/10">
                    <div className="mx-auto w-full max-w-3xl flex items-center justify-between gap-3">
                      <div className="text-sm text-muted-foreground">
                        Oops, something went wrong. Retry to continue.
                      </div>
                      <div className="flex items-center gap-2">
                        {retryError ? (
                          <span className="text-xs text-destructive">
                            {retryError}
                          </span>
                        ) : null}
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={isRetrying}
                          onClick={handleRetry}
                        >
                          <RotateCcw className="h-4 w-4 mr-1" />
                          <span>Retry</span>
                          {isRetrying ? (
                            <Loader2 className="h-4 w-4 ml-2 animate-spin" />
                          ) : null}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                <div
                  className={cn(
                    "border-t px-6 py-4 flex-shrink-0 transition-all duration-200 relative",
                    isDragOver &&
                      "bg-primary/5 border-t-primary border-dashed ring-2 ring-primary/20 ring-inset",
                  )}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (e.dataTransfer.types.includes("Files")) {
                      setIsDragOver(true);
                    }
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                      setIsDragOver(false);
                    }
                  }}
                  onDrop={handleDrop}
                >
                  {isDragOver && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-primary/5 pointer-events-none animate-in fade-in duration-150">
                      <div className="flex items-center gap-2 text-primary">
                        <Upload className="h-5 w-5" />
                        <span className="text-sm font-medium">
                          Drop files to attach
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="mx-auto w-full max-w-3xl">
                    {attachedAssets.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {attachedAssets.map((asset) => (
                          <div
                            key={asset.id}
                            className="flex items-center gap-2 bg-muted px-3 py-1.5 rounded-md text-sm"
                          >
                            <button
                              type="button"
                              className="truncate max-w-[200px] hover:underline cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                              title={asset.fileName}
                              onClick={() => setPreviewAsset(asset)}
                            >
                              {asset.fileName}
                            </button>
                            <button
                              onClick={() => handleRemoveAsset(asset.id)}
                              className="hover:text-destructive focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-sm transition-colors"
                              aria-label={`Remove ${asset.fileName}`}
                              disabled={
                                isSending ||
                                thread?.status ===
                                  AGENT_SDK_THREAD_STATUS.ACTIVE ||
                                pendingScreeningMessage !== undefined
                              }
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <ThreadReplyComposer
                      onSend={handleSendReply}
                      placeholder={
                        thread?.status === AGENT_SDK_THREAD_STATUS.ACTIVE
                          ? "Waiting for response..."
                          : pendingScreeningMessage !== undefined
                            ? "Review the flagged message before continuing..."
                            : "Send a follow-up message..."
                      }
                      isSending={isSending}
                      isUploadingFile={isUploadingFile}
                      isActive={
                        thread?.status === AGENT_SDK_THREAD_STATUS.ACTIVE
                      }
                      isScreeningBlocked={pendingScreeningMessage !== undefined}
                      onStop={handleStopThread}
                      stopRequested={thread?.stopRequested ?? false}
                      onAttachClick={() => fileInputRef.current?.click()}
                      fileInputRef={fileInputRef}
                      onFileSelect={handleFileSelect}
                    />
                  </div>
                </div>
              </div>

              {/* Splitter */}
              <div
                role="separator"
                aria-label="Resize thread correspondence pane"
                aria-controls={correspondencePaneId}
                aria-orientation="vertical"
                aria-valuemin={MIN_CORRESPONDENCE_WIDTH_PERCENT}
                aria-valuemax={MAX_CORRESPONDENCE_WIDTH_PERCENT}
                aria-valuenow={Math.round(correspondenceWidth)}
                aria-valuetext={`${Math.round(correspondenceWidth)}% correspondence width`}
                tabIndex={!panelOpen || isMobile || isChatCollapsed ? -1 : 0}
                onKeyDown={handlePanelResizeKeyDown}
                onLostPointerCapture={handlePanelResizeEnd}
                onPointerCancel={handlePanelResizeEnd}
                onPointerDown={handlePanelResizeStart}
                onPointerMove={handlePanelResizeMove}
                onPointerUp={handlePanelResizeEnd}
                className={cn(
                  "group relative z-10 flex w-px shrink-0 touch-none cursor-col-resize items-center justify-center bg-border",
                  "after:absolute after:inset-y-0 after:left-1/2 after:w-3 after:-translate-x-1/2",
                  "hover:bg-primary/60 focus-visible:bg-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1",
                  "transition-[width,opacity,background-color] duration-300 ease-in-out",
                  (!panelOpen || isMobile) &&
                    "opacity-0 w-0 pointer-events-none",
                  isChatCollapsed && "opacity-0 w-0 pointer-events-none",
                )}
              >
                <span className="z-10 flex h-8 w-3 items-center justify-center rounded-sm border border-border bg-background text-muted-foreground opacity-60 shadow-sm transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                  <GripVertical className="h-3 w-3" />
                </span>
              </div>

              {/* Rich Action Panel */}
              <div
                className={cn(
                  "flex flex-col h-full overflow-hidden bg-background",
                  isMobile
                    ? "absolute inset-0 z-50 transition-transform duration-300 ease-in-out"
                    : !isResizingPanels &&
                        "transition-all duration-300 ease-in-out",
                  isMobile && !panelOpen && "translate-x-full",
                  isMobile && panelOpen && "translate-x-0",
                  !isMobile && !panelOpen && "w-0",
                  !isMobile && panelOpen && isChatCollapsed && "w-full",
                  !isMobile &&
                    panelOpen &&
                    !isChatCollapsed &&
                    "min-w-0 flex-1",
                )}
              >
                {(panelOpen || isMobile) && (
                  <RichActionPanel
                    richActionData={richActionData}
                    agentTask={selectedAgentTask}
                    screeningMessages={messages}
                    onApprove={handleApprove}
                    onDeny={handleDeny}
                    approvingTaskId={approvingTaskId}
                    denyingTaskId={denyingTaskId}
                    onOpenChange={(open) => {
                      if (!open) {
                        dismissSelectedTask();
                      }
                    }}
                    isChatCollapsed={isChatCollapsed}
                    onToggleChat={() => setIsChatCollapsed(!isChatCollapsed)}
                    isMobile={isMobile}
                    sdkThreadId={threadId ?? undefined}
                    sdkMessageId={richActionMessageId ?? undefined}
                    refreshKey={refreshKey}
                    isThreadWorking={
                      thread?.status === AGENT_SDK_THREAD_STATUS.ACTIVE
                    }
                  />
                )}
              </div>
            </div>
            <AddShopRuleButton />
          </>
        )}
      </SheetContent>
      <AlertDialog
        open={isCancelTimerDialogOpen}
        onOpenChange={setIsCancelTimerDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel wait timer</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel the scheduled wait and end the current workflow.
              You can still send a new message to continue the conversation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep waiting</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setIsCancelTimerDialogOpen(false);
                handleCancelWaitTimer();
              }}
              className="bg-destructive hover:bg-destructive/90"
            >
              Cancel timer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CancelTaskConfirmationDialog
        open={cancelTaskId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setCancelTaskId(null);
          }
        }}
        onConfirm={confirmDeny}
        taskSubject={
          openTasks.find((task) => task.id === cancelTaskId)?.subject ??
          undefined
        }
        agentName={standardAgentName}
      />

      <AssetPreviewDialog
        assetId={previewAsset?.id ?? null}
        fileName={previewAsset?.fileName}
        onClose={() => setPreviewAsset(null)}
      />

      <AssetPreviewDialog
        assetId={summaryAssetPreview?.id ?? null}
        fileName={summaryAssetPreview?.fileName}
        onClose={() => setSummaryAssetPreview(null)}
      />

      <ThreadQuoteSheet
        quoteId={quotePreviewId}
        onClose={() => setQuotePreviewId(null)}
      />

      {opportunityId && (
        <OpportunityDetailSheet
          open={isOpportunitySheetOpen}
          onOpenChange={setIsOpportunitySheetOpen}
          opportunityId={opportunityId}
          onSave={handleSaveOpportunity}
          onArchive={handleArchiveOpportunity}
          onUnarchive={handleUnarchiveOpportunity}
          mode="edit"
          enableThreadDeepLink={false}
          readOnly
        />
      )}
    </Sheet>
  );
}

const getUserSenderInfo = (
  message: AgentSdkMessage,
  currentUser: { id: string; profilePictureUrl: string | null } | null,
  getUserById: (userId: string) => PublicUser | undefined,
): {
  name: string;
  details: string | null;
  profilePictureUrl: string | null;
} => {
  const isCurrentUser =
    message.userId !== null &&
    message.userId !== undefined &&
    currentUser !== null &&
    message.userId === currentUser.id;

  if (isCurrentUser) {
    return {
      name: "You",
      details: null,
      profilePictureUrl: currentUser.profilePictureUrl,
    };
  }

  const senderUser = message.userId ? getUserById(message.userId) : undefined;
  const profilePictureUrl = senderUser?.profilePictureUrl ?? null;

  const senderName = message.messageMetadata?.senderMetadata?.name ?? null;
  const senderEmail = message.messageMetadata?.senderMetadata?.email ?? null;
  const senderPhone =
    message.messageMetadata?.senderMetadata?.phoneNumber ?? null;

  if (senderName) {
    return { name: senderName, details: null, profilePictureUrl };
  }
  if (senderEmail && senderPhone) {
    return { name: senderEmail, details: senderPhone, profilePictureUrl };
  }
  if (senderEmail) {
    return { name: senderEmail, details: null, profilePictureUrl };
  }
  if (senderPhone) {
    return { name: senderPhone, details: null, profilePictureUrl };
  }

  if (senderUser) {
    return {
      name: getUserDisplayName(senderUser),
      details: null,
      profilePictureUrl,
    };
  }

  if (message.messageMetadata?.label) {
    return {
      name: message.messageMetadata.label,
      details: null,
      profilePictureUrl,
    };
  }

  return { name: "User", details: null, profilePictureUrl };
};

/**
 * Resolves where a message the agent moved in from another thread came from.
 * @param message - The user message
 * @param agentName - Display name of the agent that moved it
 * @returns The move source, or null when the message was not moved in
 */
const getMovedMessageSource = (
  message: AgentSdkMessage,
  agentName: string,
): MovedMessageSource | null => {
  if (message.messageMetadata?.type !== "agent_thread_message") {
    return null;
  }

  const threadId = message.messageMetadata.fields.sourceThreadId;
  if (!threadId) {
    return null;
  }

  return {
    threadId,
    threadName: message.messageMetadata.fields.sourceThreadName ?? null,
    movedByName: agentName,
  };
};
